import React, { useState, useMemo } from "react";
import PageLoader from "../components/ui/PageLoader";
import { useNavigate } from "react-router-dom";
import "../assets/css/style.css";
import { FiEdit2, FiTrash2 } from "react-icons/fi";
import { FaPlus } from "react-icons/fa6";
import Modal from "../components/ui/Modal";
import {
  useAddVendorTagMutation,
  useDeleteVendorTagMutation,
  useGetAllVendorTagsQuery,
  useUpdateVendorTagMutation,
} from "apis/commsTagVendor";
import { useForm } from "react-hook-form";
import Button from "components/ui/Button";
import Table from "components/ui/Table";

const Edit = ({ initialData, onSubmit }) => {
  const {
    register,
    handleSubmit,
    formState: { errors },
  } = useForm({
    defaultValues: {
      name: initialData?.name || "",
      description: initialData?.description || "",
    },
  });

  const submitForm = (data) => {
    onSubmit({
      name: data?.name,
      description: data?.description,
    });
  };

  return (
    <form onSubmit={handleSubmit(submitForm)} className="space-y-4 p-2">
      <div className="space-y-2">
        <label htmlFor="name" className="form-label">
          Name
        </label>
        <input
          type="text"
          id="name"
          {...register("name", { required: true })}
          placeholder="Enter name"
          className="form-input border border-mediumgray"
        />
        {errors.name && (
          <p className="form-error-message">Name is required</p>
        )}
      </div>

      <div className="space-y-2">
        <label htmlFor="description" className="form-label">
          Description
        </label>
        <textarea
          id="description"
          {...register("description")}
          placeholder="Enter description"
          rows={3}
          className="form-textarea border border-mediumgray"
        />
      </div>

      <Button type="submit" className="black-button">
        Save
      </Button>
    </form>
  );
};

const CommsTagVendor = () => {
  const pageSize = 10;
  const navigate = useNavigate();
  const [isEditOpen, setIsEditOpen] = useState(false);
  const [isDeleteOpen, setIsDeleteOpen] = useState(false);
  const [isAddOpen, setIsAddOpen] = useState(false);
  const [selectedId, setSelectedId] = useState(null);

  const { mutate: updateVendorTag } = useUpdateVendorTagMutation();
  
  const {
    data: vendorTags = [],
    isPending: isVendorTagsPending,
  } = useGetAllVendorTagsQuery();

  const { mutate: addVendorTag } = useAddVendorTagMutation();
  const { mutate: deleteVendorTag } = useDeleteVendorTagMutation();

  const columns = useMemo(
    () => [
      {
        accessorKey: "index",
        header: "#",
        cell: (_, rowIndex, currentPage) =>
          (currentPage - 1) * pageSize + rowIndex + 1,
        isFilter: false,
        isSortable: false,
      },
      {
        accessorKey: "name",
        header: "Name",
        isFilter: true,
      },
      {
        accessorKey: "description",
        header: "Description",
        isFilter: true,
      },
      {
        accessorKey: "actions",
        header: "Actions",
        cell: (row) => (
          <div className="flex items-center space-x-4">
            <button
              onClick={() => {
                setSelectedId(row._id);
                setIsEditOpen(true);
              }}
              className="edit-icon"
              title="Edit Vendor Tag"
            >
              <FiEdit2 fontSize={20} />
            </button>
            <button
              onClick={() => {
                setSelectedId(row._id);
                setIsDeleteOpen(true);
              }}
              className="delete-icon"
              title="Delete Vendor Tag"
            >
              <FiTrash2 fontSize={20} />
            </button>
          </div>
        ),
        isFilter: false,
        isSortable: false,
        stopPropagation: true,
        minWidth: "120px",
      },
    ],
    [navigate] // eslint-disable-line react-hooks/exhaustive-deps
  );

  if (isVendorTagsPending) {
    return <PageLoader />;
  }

  const handleDelete = () => {
    deleteVendorTag(selectedId);
    setIsDeleteOpen(false);
  };

  const AddVendorTagButton = () => (
    <button
      className="black-button flex items-center"
      onClick={() => setIsAddOpen(true)}
    >
      <FaPlus size={16} className="mr-2" />
      Add Vendor Tag
    </button>
  );

  return (
    <>
      <Modal
        isOpen={isEditOpen}
        onClose={() => setIsEditOpen(false)}
        title="Vendor Tag Edit"
        className="max-w-2xl"
      >
        <Edit
          initialData={vendorTags.find((tag) => tag._id === selectedId)}
          onSubmit={(formData) => {
            updateVendorTag({ id: selectedId, ...formData });
            setIsEditOpen(false);
          }}
        />
      </Modal>

      <Modal
        isOpen={isAddOpen}
        onClose={() => setIsAddOpen(false)}
        title="Add Vendor Tag"
        className="max-w-2xl"
      >
        <Edit
          initialData=""
          onSubmit={(formData) => {
            addVendorTag(formData);
            setIsAddOpen(false);
          }}
        />
      </Modal>

      <Modal
        isOpen={isDeleteOpen}
        onClose={() => setIsDeleteOpen(false)}
        title="Delete Tag"
      >
        <div>
          Are you sure you want to delete this Tag?
          <div className="w-full flex gap-2 justify-end py-4">
            <Button
              onClick={() => setIsDeleteOpen(false)}
              className="gray-button"
            >
              No
            </Button>
            <Button onClick={handleDelete} className="black-button">
              Yes
            </Button>
          </div>
        </div>
      </Modal>

      <div className="container mx-auto">
        <Table
          title="Vendor Tags"
          columns={columns}
          data={vendorTags || []}
          pageSize={pageSize}
          ButtonCmp={AddVendorTagButton}
        />
      </div>
    </>
  );
};

export default CommsTagVendor;
